// src/components/firstAider/ActiveCalloutCards.tsx — the prominent "live
// emergency" card(s) shown at the very top of Home (and above the compose form).
//
// As soon as a first-aider callout is active, this card takes the top of the
// screen for everyone involved — the CALLER (so they can get back to it),
// every recipient (to confirm attendance), and admins (oversight). It shows the
// emergency, a live responder tally, and a role-aware call-to-action; tapping
// opens the full incident thread (briefing -> respond -> responders -> resolve).
//
// Polls every 15s so the tally/status stay current while you sit on Home.
// Renders nothing when there's no active callout, so Home is unchanged otherwise.

import type { CSSProperties } from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { C, F } from "../../theme";
import { ApiError } from "../../api";
import {
  FA,
  callerName,
  fetchActiveCallouts,
  type ActiveCallout,
} from "../../lib/firstAider";
import { timeSinceLabel } from "../../lib/quickMessages";
import { EmergencyGlyph, FaKeyframes, PulseDot } from "./kit";

const POLL_MS = 15_000;

export function ActiveCalloutCards() {
  const [callouts, setCallouts] = useState<ActiveCallout[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      fetchActiveCallouts()
        .then((res) => {
          if (!cancelled) setCallouts(res.callouts);
        })
        .catch((e) => {
          if (!cancelled) {
            if (!(e instanceof ApiError)) console.error("[ActiveCalloutCards] load failed", e);
            setCallouts([]);
          }
        });
    load();
    const id = window.setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  if (!callouts || callouts.length === 0) return null;

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {callouts.map((c) => (
          <CalloutCard key={c.incidentId} c={c} />
        ))}
      </div>
      <FaKeyframes />
    </>
  );
}

interface Cta {
  text: string;
  urgent: boolean;
  aria: string;
}

function ctaFor(c: ActiveCallout): Cta {
  // Status first: if I hold an unanswered alert (even as the caller, now that a
  // first-aider caller is also a responder), prompt me to confirm attendance.
  if (c.myStatus === "pending" || c.myStatus === "read") {
    return { text: "Tap here to confirm your attendance", urgent: true, aria: "Tap here to confirm your attendance." };
  }
  if (c.myStatus === "on_way") {
    return { text: "You're on your way — tap to view", urgent: false, aria: "You are on your way. Tap to view the thread." };
  }
  if (c.myStatus === "declined") {
    return { text: "You can't attend — tap to view", urgent: false, aria: "You said you cannot attend. Tap to view the thread." };
  }
  if (c.myRole === "caller") {
    return { text: "Your callout — tap to view responders", urgent: false, aria: "Your callout. Tap to view responders." };
  }
  return { text: "Tap to view / manage", urgent: false, aria: "Tap to view or manage this callout." };
}

function CalloutCard({ c }: { c: ActiveCallout }) {
  const navigate = useNavigate();
  const since = timeSinceLabel(c.createdAt);
  const cta = ctaFor(c);

  return (
    <button
      type="button"
      onClick={() => navigate(`/first-aider/incidents/${c.incidentId}`)}
      aria-label={`Active first aider callout at ${c.location}. ${cta.aria}`}
      style={cardRoot}
    >
      <span style={hatch} aria-hidden="true" />
      <span style={header}>
        <span style={eyebrow}>
          <PulseDot tone="white" />
          Active first aid callout · {since.toLowerCase()}
        </span>
        <span style={headline}>
          <span style={iconWrap}>
            <EmergencyGlyph size={20} color="#FFFFFF" />
          </span>
          Emergency dispatch
        </span>
      </span>

      <span style={body}>
        <span style={fieldTable}>
          <FieldRow label="Location" value={c.location} strong />
          <FieldRow label="Who is injured" value={c.casualty} />
          <FieldRow label="Life-threatening" badge={{ text: c.isLifeThreatening ? "YES" : "No", red: c.isLifeThreatening }} />
          <FieldRow label="999 called" badge={{ text: c.called999 ? "YES" : "No", red: c.called999 }} />
          <FieldRow label="Nature of injury" value={c.nature} />
          <FieldRow label="Reported by" value={callerName(c.caller)} phone={c.caller.phone} last />
        </span>
        <span style={tallyRow}>
          <TallyCol n={c.tally.onWay} label="On the way" color={FA.go} />
          <TallyCol n={c.tally.cannot} label="Cannot" color={FA.stop} />
          <TallyCol n={c.tally.pending} label="Pending" color={FA.pending} />
        </span>
        <span style={{ ...ctaStrip, ...(cta.urgent ? ctaUrgent : ctaNormal) }}>
          <span>{cta.text}</span>
          <span style={chevron}>›</span>
        </span>
      </span>
    </button>
  );
}

function TallyCol({ n, label, color }: { n: number; label: string; color: string }) {
  return (
    <span style={{ flex: 1, textAlign: "center", display: "block" }}>
      <span
        style={{
          display: "block",
          fontFamily: F.serif,
          fontSize: 24,
          fontWeight: 700,
          letterSpacing: -0.4,
          lineHeight: 1,
          color,
        }}
      >
        {n}
      </span>
      <span
        style={{
          display: "block",
          fontFamily: F.body,
          fontSize: 9.5,
          fontWeight: 800,
          letterSpacing: 0.9,
          textTransform: "uppercase",
          marginTop: 4,
          color,
        }}
      >
        {label}
      </span>
    </span>
  );
}

function FieldRow({
  label,
  value,
  badge,
  phone,
  strong = false,
  last = false,
}: {
  label: string;
  value?: string;
  badge?: { text: string; red: boolean };
  phone?: string | null;
  strong?: boolean;
  last?: boolean;
}) {
  return (
    <span style={{ ...fieldRow, borderBottom: last ? "none" : `1px solid ${C.hairlineLt}` }}>
      <span style={fieldLabel}>{label}</span>
      <span style={{ display: "block", textAlign: "right", minWidth: 0, flex: 1 }}>
        {badge ? (
          <span style={badge.red ? badgeRed : badgeMute}>{badge.text}</span>
        ) : (
          <>
            <span style={{ ...fieldValue, fontSize: strong ? 16 : 14 }}>{value}</span>
            {phone ? <span style={fieldPhone}>{phone}</span> : null}
          </>
        )}
      </span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const cardRoot: CSSProperties = {
  display: "block",
  width: "100%",
  padding: 0,
  border: `1.5px solid ${FA.red}`,
  borderRadius: 18,
  overflow: "hidden",
  background: C.card,
  boxShadow: "0 6px 20px rgba(184,54,42,0.28)",
  cursor: "pointer",
  textAlign: "left",
  position: "relative",
  fontFamily: F.body,
  WebkitTapHighlightColor: "transparent",
};

const hatch: CSSProperties = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  height: 96,
  background:
    "repeating-linear-gradient(45deg, transparent, transparent 12px, rgba(255,255,255,0.03) 12px, rgba(255,255,255,0.03) 24px)",
  pointerEvents: "none",
  zIndex: 1,
};

const header: CSSProperties = {
  display: "block",
  position: "relative",
  zIndex: 2,
  padding: "14px 16px 14px",
  background: `linear-gradient(180deg, ${FA.red} 0%, ${FA.redDk} 100%)`,
  color: "#FFFFFF",
};

const eyebrow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 7,
  fontFamily: F.body,
  fontSize: 10,
  fontWeight: 800,
  letterSpacing: 1.4,
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.95)",
  marginBottom: 5,
};

const headline: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 9,
  fontFamily: F.serif,
  fontSize: 22,
  fontWeight: 700,
  letterSpacing: -0.3,
  color: "#FFFFFF",
  lineHeight: 1.1,
};

const iconWrap: CSSProperties = {
  width: 34,
  height: 34,
  borderRadius: 10,
  background: "rgba(255,255,255,0.18)",
  border: "1px solid rgba(255,255,255,0.3)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const fieldTable: CSSProperties = {
  display: "block",
  paddingBottom: 2,
};

const fieldRow: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 12,
  padding: "9px 0",
};

const fieldLabel: CSSProperties = {
  fontFamily: F.body,
  fontSize: 9.5,
  fontWeight: 800,
  letterSpacing: 0.9,
  textTransform: "uppercase",
  color: FA.red,
  paddingTop: 3,
  flexShrink: 0,
};

const fieldValue: CSSProperties = {
  display: "block",
  fontFamily: F.serif,
  fontWeight: 600,
  color: C.ink,
  lineHeight: 1.3,
};

const fieldPhone: CSSProperties = {
  display: "block",
  fontFamily: F.mono,
  fontSize: 13,
  fontWeight: 700,
  color: FA.redDk,
  letterSpacing: 0.4,
  marginTop: 2,
};

const badgeRed: CSSProperties = {
  display: "inline-block",
  padding: "3px 10px",
  borderRadius: 999,
  background: FA.red,
  color: "#FFFFFF",
  fontFamily: F.body,
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 0.5,
};

const badgeMute: CSSProperties = {
  display: "inline-block",
  padding: "3px 10px",
  borderRadius: 999,
  background: C.paperWarm,
  color: C.inkSoft,
  fontFamily: F.body,
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 0.5,
};

const body: CSSProperties = {
  display: "block",
  padding: "13px 16px 14px",
  background: C.card,
};

const tallyRow: CSSProperties = {
  display: "flex",
  gap: 8,
  paddingBottom: 13,
  marginBottom: 13,
  borderBottom: `1px solid ${C.hairlineLt}`,
};

const ctaStrip: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
  borderRadius: 12,
  padding: "13px 16px",
  fontFamily: F.body,
  fontSize: 15,
  fontWeight: 800,
  letterSpacing: 0.2,
};

const ctaUrgent: CSSProperties = {
  background: `linear-gradient(180deg, ${FA.go} 0%, #355C43 100%)`,
  color: "#FFFFFF",
  boxShadow: "0 3px 10px rgba(63,107,79,0.3)",
};

const ctaNormal: CSSProperties = {
  background: FA.tint,
  color: FA.redDk,
  border: `1px solid ${FA.redLt}`,
};

const chevron: CSSProperties = {
  fontSize: 20,
  fontWeight: 700,
  flexShrink: 0,
};
